import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Avatar,
  Button,
  alpha,
} from '@mui/material';
import {
  Edit as EditIcon,
  Archive as ArchiveIcon,
  Build as MaintenanceIcon,
  Person as PersonIcon,
  LocationOn as LocationIcon,
  Flag as FlagIcon,
  Category as CategoryIcon,
  AccessTime as AccessTimeIcon,
  CheckCircle as CheckCircleIcon,
  ErrorOutline as ErrorOutlineIcon,
  Assignment as AssignmentIcon,
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material';
import { format, isValid, formatDistanceToNow } from 'date-fns';
import api from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { canModifyMaintenanceRequest, canArchiveMaintenanceRequest } from '../../../utils/maintenancePermissions';
import { resolveUserId, userDetailsPath } from '../../../utils/userDetailsLink';
import Header from '../../common/Header';
import Loading from '../../common/Loading';
import FeedbackMessage from '../../common/FeedbackMessage';
import ConfirmDeleteDialog from '../../common/ConfirmDeleteDialog';
import {
  injectDetailsPageStyles,
} from '../../common/DetailsPagePrimitives';
import SystemInformationSection from '../../common/SystemInformationSection';
import { formatSystemDateTime } from '../../../utils/systemDateFormat';
import { toStorageUrl } from '../../../utils/storageUrl';

// â”€â”€â”€ Design tokens â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const B            = { 600: '#2563EB', 700: '#1D4ED8', 800: '#1E40AF', 900: '#1E3A8A' };
const NEON         = '#38BDF8';
const NEON2        = '#818CF8';
const NEON3        = '#34D399';
const SURFACE_BASE = '#050d1a';
const SURFACE_MID  = '#0a1628';
const SURFACE_UP   = '#0f1f38';
const SURFACE_TOP  = '#152640';
const TEXT_BODY    = '#E2E8F0';
const TEXT_MUTED   = '#64748B';

// â”€â”€â”€ Helpers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const getStatusMeta = (s) => ({
  pending:     { color: NEON2,     icon: <AccessTimeIcon />,   label: 'Pending' },
  in_progress: { color: NEON,      icon: <TrendingUpIcon />,   label: 'In Progress' },
  completed:   { color: NEON3,     icon: <CheckCircleIcon />,  label: 'Completed' },
  archived:    { color: '#94A3B8', icon: <ArchiveIcon />,      label: 'Archived' },
}[s] ?? { color: NEON, icon: <MaintenanceIcon />, label: s ?? '-' });

const getPriorityMeta = (p) => ({
  low:    { color: NEON3,     icon: <FlagIcon />,        label: 'Low' },
  medium: { color: NEON,      icon: <InfoIcon />,         label: 'Medium' },
  high:   { color: NEON2,     icon: <WarningIcon />,      label: 'High' },
  urgent: { color: '#F87171', icon: <ErrorOutlineIcon />, label: 'Urgent' },
}[p] ?? { color: NEON, icon: <FlagIcon />, label: p ?? '-' });

const getTypeMeta = (t) => ({
  repair:      { color: NEON,      label: 'Repair' },
  replacement: { color: NEON2,     label: 'Replacement' },
  inspection:  { color: NEON3,     label: 'Inspection' },
  other:       { color: TEXT_MUTED, label: 'Other' },
}[t] ?? { color: NEON, label: t ? t.charAt(0).toUpperCase() + t.slice(1) : '-' });

const fmtDate     = (d) => { if (!d) return null; const dt = new Date(d); return isValid(dt) ? format(dt, 'MMM dd, yyyy') : null; };
const fmtRelative = (d) => { if (!d) return null; try { return formatDistanceToNow(new Date(d), { addSuffix: true }); } catch { return null; } };
const formatAuditUser = (u) => (u?.name ? String(u.name) : null);

// â”€â”€â”€ StatusBadge â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const StatusBadge = ({ color, label }) => (
  <Box sx={{
    display: 'flex', alignItems: 'center', gap: 0.7,
    px: 1.4, py: 0.5, borderRadius: '20px',
    background: alpha(color, 0.1),
    border: `1px solid ${alpha(color, 0.3)}`,
    alignSelf: 'flex-start',
  }}>
    <Box sx={{ width: 7, height: 7, borderRadius: '50%', background: color }} />
    <Typography sx={{ fontSize: '0.75rem', fontFamily: "'DM Sans', sans-serif", fontWeight: 700, color, letterSpacing: '0.05em' }}>
      {label}
    </Typography>
  </Box>
);

// â”€â”€â”€ PersonRow â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const PersonRow = ({ label, person, neon = NEON2, onNavigate }) => {
  const userId = resolveUserId(person);
  const canNavigate = Boolean(userId && onNavigate);
  return (
  <Box>
    <Typography sx={{
      fontFamily: "'JetBrains Mono', monospace",
      fontSize: '0.58rem', fontWeight: 600,
      letterSpacing: 1.6, textTransform: 'uppercase',
      color: alpha(NEON, 0.45), mb: 1,
    }}>
      {label}
    </Typography>
    {person ? (
      <Box
        role={canNavigate ? 'link' : undefined}
        tabIndex={canNavigate ? 0 : undefined}
        onClick={canNavigate ? () => onNavigate(userDetailsPath(userId)) : undefined}
        onKeyDown={canNavigate ? (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onNavigate(userDetailsPath(userId));
          }
        } : undefined}
        sx={{
        display: 'flex', alignItems: 'center', gap: 1.5,
        p: 1.75, borderRadius: '10px',
        background: alpha(SURFACE_UP, 0.5),
        border: `1px solid ${alpha(neon, 0.15)}`,
        cursor: canNavigate ? 'pointer' : 'default',
        transition: 'background 0.15s',
        '&:hover': canNavigate ? { background: alpha(neon, 0.08) } : {},
      }}>
        <Avatar
          src={person.profile_picture ? toStorageUrl(person.profile_picture_url || person.profile_picture) : undefined}
          sx={{
            width: 38, height: 38,
            background: `linear-gradient(135deg, ${alpha(neon, 0.3)}, ${alpha(B[700], 0.6)})`,
            border: `2px solid ${alpha(neon, 0.3)}`,
            fontSize: '0.85rem', fontWeight: 600, color: neon,
          }}
        >
          {person.name?.charAt(0).toUpperCase()}
        </Avatar>
        <Box>
          <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.88rem', fontWeight: 600, color: canNavigate ? neon : TEXT_BODY }}>
            {person.name}
          </Typography>
          <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.76rem', color: TEXT_MUTED }}>
            {person.email}
          </Typography>
        </Box>
      </Box>
    ) : (
      <Box sx={{
        p: 1.75, borderRadius: '10px',
        background: alpha(SURFACE_UP, 0.3),
        border: `1px dashed ${alpha(NEON, 0.12)}`,
      }}>
        <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.83rem', color: alpha(TEXT_MUTED, 0.6), fontStyle: 'italic' }}>
          Not assigned
        </Typography>
      </Box>
    )}
  </Box>
  );
};

const MetricCard = ({ label, value, icon: Icon, color = NEON, sub, onClick }) => (
  <Box
    role={onClick ? 'link' : undefined}
    tabIndex={onClick ? 0 : undefined}
    onClick={onClick}
    onKeyDown={onClick ? (e) => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(e); }
    } : undefined}
    sx={{
    position: 'relative',
    borderRadius: '14px', px: 2.5, py: 2,
    background: `linear-gradient(145deg, ${alpha(color, 0.09)} 0%, ${alpha(SURFACE_UP, 0.6)} 100%)`,
    border: `1px solid ${alpha(color, 0.2)}`,
    boxShadow: `0 0 24px ${alpha(color, 0.07)}, inset 0 1px 0 ${alpha(color, 0.1)}`,
    overflow: 'hidden',
    cursor: onClick ? 'pointer' : 'default',
  }}>
    <Box sx={{ position: 'absolute', top: -16, right: -16, width: 70, height: 70, borderRadius: '50%', background: alpha(color, 0.08), filter: 'blur(18px)', pointerEvents: 'none' }} />
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.8 }}>
      {Icon && <Icon sx={{ fontSize: 13, color: alpha(color, 0.75) }} />}
      <Typography sx={{ fontSize: '0.67rem', fontFamily: "'DM Sans', sans-serif", color: TEXT_MUTED, letterSpacing: '0.09em', textTransform: 'uppercase', fontWeight: 600 }}>
        {label}
      </Typography>
    </Box>
    <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, fontFamily: "'Rajdhani', sans-serif", color, letterSpacing: 0.3, lineHeight: 1.15 }}>
      {value || '—'}
    </Typography>
    {sub && <Typography sx={{ fontSize: '0.7rem', color: TEXT_MUTED, fontFamily: "'DM Sans', sans-serif", mt: 0.4 }}>{sub}</Typography>}
  </Box>
);

// â”€â”€â”€ SectionCard wrapper â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const SectionCard = ({ title, icon, delay = 0, children, accentColor }) => (
  <Box sx={{
    borderRadius: '16px',
    border: `1px solid ${alpha(accentColor ?? NEON, 0.14)}`,
    background: `linear-gradient(160deg, ${alpha(SURFACE_UP, 0.75)} 0%, ${alpha(SURFACE_MID, 0.95)} 100%)`,
    overflow: 'hidden',
    animation: `fadeUp 0.4s ease ${delay}s both`,
    '@keyframes fadeUp': {
      from: { opacity: 0, transform: 'translateY(10px)' },
      to: { opacity: 1, transform: 'translateY(0)' },
    },
  }}>
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1.2,
      px: 2.5, py: 1.5,
      borderBottom: `1px solid ${alpha(accentColor ?? NEON, 0.1)}`,
      background: alpha(accentColor ?? NEON, 0.04),
    }}>
      <Box sx={{
        width: 26, height: 26, borderRadius: '7px',
        background: alpha(accentColor ?? NEON, 0.12),
        border: `1px solid ${alpha(accentColor ?? NEON, 0.22)}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {React.isValidElement(icon)
          ? React.cloneElement(icon, { sx: { fontSize: 13, color: accentColor ?? NEON } })
          : null}
      </Box>
      <Typography sx={{
        fontFamily: "'DM Sans', sans-serif", fontWeight: 700,
        fontSize: '0.78rem', color: alpha(TEXT_BODY, 0.85),
        letterSpacing: '0.07em', textTransform: 'uppercase',
      }}>
        {title}
      </Typography>
    </Box>
    <Box sx={{ p: 2 }}>{children}</Box>
  </Box>
);

// â”€â”€â”€ InfoField wrapper â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const InfoField = ({ icon, label, value, fullWidth = false }) => (
  <Box sx={{
    gridColumn: fullWidth ? '1 / -1' : undefined,
    display: 'flex', alignItems: 'center', gap: 1.5,
    py: 0.9, px: 1.2, borderRadius: '9px',
  }}>
    <Box sx={{
      width: 30, height: 30, borderRadius: '8px', flexShrink: 0,
      background: alpha(NEON, 0.1),
      border: `1px solid ${alpha(NEON, 0.18)}`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      {React.cloneElement(icon, { sx: { fontSize: 14, color: alpha(NEON, 0.85) } })}
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography sx={{ fontSize: '0.65rem', color: TEXT_MUTED, fontFamily: "'DM Sans', sans-serif", textTransform: 'uppercase', letterSpacing: '0.07em', fontWeight: 600 }}>
        {label}
      </Typography>
      <Box sx={{ fontSize: '0.88rem', color: TEXT_BODY, fontFamily: "'DM Sans', sans-serif", fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {value || '-'}
      </Box>
    </Box>
  </Box>
);

/** Same shell as RequirementDetails "Purpose" — pass body as `children` (e.g. Typography) */
const PurposeStyleCallout = ({ children, accent = NEON, sx = {} }) => (
  <Box
    sx={{
      px: 2,
      py: 1.8,
      borderRadius: '10px',
      borderLeft: `3px solid ${alpha(accent, 0.4)}`,
      background: alpha(SURFACE_TOP, 0.4),
      ...sx,
    }}
  >
    {children}
  </Box>
);

// â”€â”€â”€ ActionButtons â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const ActionButtons = ({ canModify, canArchive, onEdit, onArchive }) => {
  if (!canModify && !canArchive) return null;
  return (
    <Box sx={{ display: 'flex', gap: 1.5 }}>
      {canModify && (
        <Button
          startIcon={<EditIcon sx={{ fontSize: 16 }} />}
          onClick={onEdit}
          sx={{
            borderRadius: '10px', px: 2.5, py: 0.7,
            fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.85rem',
            background: 'transparent', border: `1.5px solid ${alpha(NEON, 0.6)}`,
            color: NEON, transition: 'all 0.2s ease',
            '&:hover': { background: alpha(NEON, 0.08), borderColor: NEON, boxShadow: `0 0 12px ${alpha(NEON, 0.2)}` },
          }}
        >
          Edit
        </Button>
      )}
      {canArchive && (
        <Button
          startIcon={<ArchiveIcon sx={{ fontSize: 16 }} />}
          onClick={onArchive}
          sx={{
            borderRadius: '10px', px: 2.5, py: 0.7,
            fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.85rem',
            background: 'transparent', border: `1.5px solid ${alpha('#F87171', 0.6)}`,
            color: '#F87171', transition: 'all 0.2s ease',
            '&:hover': { background: alpha('#F87171', 0.08), borderColor: '#F87171', boxShadow: `0 0 12px ${alpha('#F87171', 0.2)}` },
          }}
        >
          Archive
        </Button>
      )}
    </Box>
  );
};

// â”€â”€â”€ Main Component â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const MaintenanceDetails = () => {
  const { id }                = useParams();
  const navigate              = useNavigate();
  const { user: currentUser } = useAuth();

  const [maintenance,      setMaintenance]      = useState(null);
  const [loading,          setLoading]          = useState(true);
  const [error,            setError]            = useState(null);
  const [successMessage,   setSuccessMessage]   = useState(null);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);

  const canModify = maintenance ? canModifyMaintenanceRequest(currentUser, maintenance) : false;
  const canArchive = maintenance ? canArchiveMaintenanceRequest(currentUser, maintenance) : false;

  useEffect(() => { injectDetailsPageStyles(); }, []);

  useEffect(() => {
    if (!currentUser) { navigate('/login'); return; }
    fetchMaintenance();
  }, [currentUser, id, navigate]);

  const fetchMaintenance = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/maintenances/${id}`);
      setMaintenance(response.data);
      setError(null);
    } catch {
      setError('Failed to load maintenance request');
    } finally {
      setLoading(false);
    }
  };

  const handleArchiveMaintenance = async () => {
    try {
      const title = maintenance?.title || 'Maintenance Request';
      await api.post(`/maintenances/${id}/archive`);
      setMaintenance((prev) => (prev ? { ...prev, archived_at: new Date().toISOString() } : prev));
      setArchiveDialogOpen(false);
      setSuccessMessage(`"${title}" has been archived.`);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to archive maintenance request');
      setArchiveDialogOpen(false);
    }
  };

  // â”€â”€ Guards â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      <Loading message="Loading maintenance details" size={56} variant="primary" />
    </Box>
  );

  if (error && !maintenance) return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <FeedbackMessage message={error} severity="error" sx={{ mb: 2 }} />
      <Button
        onClick={fetchMaintenance}
        sx={{
          borderRadius: '10px', px: 2.2, py: 0.65,
          fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.83rem',
          background: 'transparent', border: `1.5px solid ${alpha(NEON, 0.55)}`,
          color: NEON, transition: 'all 0.2s',
          '&:hover': { background: alpha(NEON, 0.08), borderColor: NEON, boxShadow: `0 0 14px ${alpha(NEON, 0.2)}` },
        }}
      >
        Try Again
      </Button>
    </Container>
  );

  if (!maintenance) return null;

  const effectiveStatus = maintenance.archived_at ? 'archived' : maintenance.status;
  const statusMeta   = getStatusMeta(effectiveStatus);
  const priorityMeta = getPriorityMeta(maintenance.priority);
  const typeMeta     = getTypeMeta(maintenance.type);
  const assignedToId = resolveUserId(maintenance.assigned_to ?? maintenance.assigned_to_id);
  const assigneeNames = (maintenance.assignees || []).map((member) => member.name).filter(Boolean).join(', ');

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 6 }}>
      <Box>

          {/* â”€â”€ Header â”€â”€ */}
          <Header
            title="Maintenance Details"
            subtitle="View maintenance information"
            icon={MaintenanceIcon}
            showBackButton
            onBackClick={() => navigate(-1)}
            backButtonText="Back to Maintenance"
            actionButton={
              <ActionButtons
                canModify={canModify}
                canArchive={canArchive}
                onEdit={() => navigate(`/maintenances/${id}/edit`)}
                onArchive={() => setArchiveDialogOpen(true)}
              />
            }
          />

          {/* â”€â”€ Feedback â”€â”€ */}
          <FeedbackMessage
            message={error}
            severity="error"
            onClose={() => setError(null)}
            sx={{
              fontFamily: "'DM Sans', sans-serif", fontSize: '0.9rem', fontWeight: 500,
              background: alpha('#450a0a', 0.92),
              border: '1px solid rgba(248,113,113,0.3)',
              boxShadow: '0 4px 20px rgba(239,68,68,0.12)',
              color: '#fca5a5', mb: 3,
            }}
          />
          <FeedbackMessage
            message={successMessage}
            severity="success"
            onClose={() => setSuccessMessage(null)}
            sx={{
              fontFamily: "'DM Sans', sans-serif", fontSize: '0.9rem', fontWeight: 500,
              background: `linear-gradient(135deg, ${alpha(NEON3, 0.1)}, ${alpha(SURFACE_MID, 0.95)})`,
              border: `1px solid ${alpha(NEON3, 0.35)}`,
              boxShadow: `0 4px 20px ${alpha(NEON3, 0.15)}`,
              color: alpha(NEON3, 0.95), mb: 2,
            }}
          />

          {/* â”€â”€ Sections: grouped by category â”€â”€ */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <Box sx={{
              borderRadius: '16px',
              border: `1px solid ${alpha(NEON, 0.15)}`,
              background: `linear-gradient(135deg, ${alpha(SURFACE_UP, 0.9)} 0%, ${alpha(SURFACE_MID, 0.98)} 100%)`,
              p: { xs: 2, md: 2.5 },
              animation: 'fadeUp 0.35s ease both',
            }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 2.5, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{
                    width: 48, height: 48, borderRadius: '13px', flexShrink: 0,
                    background: `linear-gradient(135deg, ${alpha(NEON2, 0.25)}, ${alpha(NEON, 0.15)})`,
                    border: `2px solid ${alpha(NEON2, 0.3)}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <MaintenanceIcon sx={{ fontSize: 22, color: NEON2 }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: '1.4rem', fontWeight: 700, fontFamily: "'Rajdhani', sans-serif", color: TEXT_BODY, letterSpacing: 0.3, lineHeight: 1.15 }}>
                      {maintenance.title || 'Untitled Request'}
                    </Typography>
                    <Typography sx={{ fontSize: '0.78rem', color: TEXT_MUTED, fontFamily: "'DM Sans', sans-serif" }}>
                      {maintenance.location || 'No location specified'}
                    </Typography>
                  </Box>
                </Box>
                <StatusBadge color={statusMeta.color} label={statusMeta.label} />
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 1.5 }}>
                <MetricCard label="Priority" value={priorityMeta.label} icon={FlagIcon} color={priorityMeta.color} />
                <MetricCard label="Type" value={typeMeta.label} icon={CategoryIcon} color={typeMeta.color} />
                <MetricCard label="Reported Date" value={fmtDate(maintenance.reported_date) ?? '-'} icon={CalendarIcon} color={NEON2} />
                <MetricCard label="Assigned To" value={assigneeNames || maintenance.assigned_to?.name} icon={PersonIcon} color={NEON3} onClick={!assigneeNames && assignedToId ? () => navigate(userDetailsPath(assignedToId)) : undefined} />
              </Box>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
              {/* Request Overview */}
              <SectionCard title="Request Overview" icon={<MaintenanceIcon />} delay={0.1}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
                  <Typography sx={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: '0.58rem', fontWeight: 600,
                    letterSpacing: 1.6, textTransform: 'uppercase',
                    color: alpha(NEON, 0.45),
                  }}>
                    Description
                  </Typography>
                  <PurposeStyleCallout accent={NEON}>
                    <Typography sx={{
                      fontFamily: "'DM Sans', sans-serif",
                      fontSize: '0.92rem',
                      lineHeight: 1.75,
                      whiteSpace: 'pre-wrap',
                      color: maintenance.description?.trim() ? TEXT_BODY : alpha(TEXT_MUTED, 0.65),
                      fontStyle: maintenance.description?.trim() ? 'normal' : 'italic',
                    }}>
                      {maintenance.description?.trim() || '-'}
                    </Typography>
                  </PurposeStyleCallout>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 0.3 }}>
                    <InfoField icon={<CalendarIcon />} label="Reported Date" value={fmtDate(maintenance.reported_date) ?? '-'} />
                    <InfoField icon={<AccessTimeIcon />} label="Reported" value={fmtRelative(maintenance.reported_date) ?? '-'} />
                    <InfoField icon={<CheckCircleIcon />} label="Completion Date" value={fmtDate(maintenance.completion_date) ?? '-'} />
                  </Box>
                </Box>
              </SectionCard>

              {/* Assignment */}
              <SectionCard title="Assignment" icon={<AssignmentIcon />} delay={0.14} accentColor={NEON2}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <PersonRow label="Reported By" person={maintenance.reported_by} neon={NEON2} onNavigate={navigate} />
                  {(maintenance.assignees?.length ? maintenance.assignees : [maintenance.assigned_to]).filter(Boolean).map((member, index) => (
                    <PersonRow key={member.id || index} label={index === 0 ? 'Assigned To' : 'Also Assigned'} person={member} neon={NEON} onNavigate={navigate} />
                  ))}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography sx={{
                      fontFamily: "'JetBrains Mono', monospace",
                      fontSize: '0.58rem', fontWeight: 600,
                      letterSpacing: 1.6, textTransform: 'uppercase',
                      color: alpha(NEON, 0.45),
                    }}>
                      Note
                    </Typography>
                    <PurposeStyleCallout accent={NEON2}>
                      <Typography sx={{
                        fontFamily: "'DM Sans', sans-serif",
                        fontSize: '0.92rem',
                        lineHeight: 1.75,
                        whiteSpace: 'pre-wrap',
                        color: maintenance.note?.trim() ? TEXT_BODY : alpha(TEXT_MUTED, 0.65),
                        fontStyle: maintenance.note?.trim() ? 'normal' : 'italic',
                      }}>
                        {maintenance.note?.trim() || '-'}
                      </Typography>
                    </PurposeStyleCallout>
                  </Box>
                </Box>
              </SectionCard>
            </Box>
            <SystemInformationSection
              createdBy={formatAuditUser(maintenance.reported_by)}
              createdAt={formatSystemDateTime(maintenance.created_at)}
              updatedBy={
                formatAuditUser(maintenance.last_updater) ||
                formatAuditUser(maintenance.updated_by_user) ||
                (typeof maintenance.updated_by === 'string' ? maintenance.updated_by : '-')
              }
              updatedAt={formatSystemDateTime(maintenance.updated_at)}
              delay={0.2}
              accentColor={NEON}
              surfaceUp={SURFACE_UP}
              surfaceMid={SURFACE_MID}
              textBody={TEXT_BODY}
              textMuted={TEXT_MUTED}
            />

          </Box>

          <ConfirmDeleteDialog
            open={archiveDialogOpen}
            onClose={() => setArchiveDialogOpen(false)}
            onConfirm={handleArchiveMaintenance}
            title="Archive Maintenance"
            itemType="maintenance request"
            itemName={maintenance?.title}
            description="The maintenance request will be archived and remain in the system for history and audit purposes."
            confirmLabel="Archive Maintenance"
            actionVerb="archive"
          />

      </Box>
    </Container>
  );
};

export default MaintenanceDetails;
